import React, { useState } from "react";

import PlusCircleIcon from "@uui/PlusCircle";

import Typography from "@mui/material/Typography";

import useCustomVocabulary from "@app/analytics/hooks/settings/useCustomVocabulary";
import type {
  CustomVocabulary,
  PatchCustomVocabularyBody,
  PostCustomVocabularyBody,
} from "@app/analytics/types";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { showToast } from "@app/common/hooks/useToasts";
import { pluralize, pluralizePhrase } from "@app/common/utils";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import AddCustomVocabularyModal from "./AddCustomVocabularyModal";
import CustomVocabularyTable from "./CustomVocabularyTable";
import DeleteWorkspaceWordConfirmationModal from "./DeleteWorkspaceWordConfirmationModal";

const CustomVocabularyModuleContent = ({
  inWorkspace,
  isLoading,
}: {
  inWorkspace: boolean;
  isLoading: boolean;
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [wordIdToConfirmDelete, setWordIdToConfirmDelete] = useState<
    CustomVocabulary["id"] | null
  >(null);
  const [confirmationDismissed, setConfirmationDismissed] = useLocalStorage(
    "deleteWorkspaceWordConfirmationDismissed",
    false,
    { migrateKeys: true }
  );

  const handleDismissConfirmation = (
    event: React.SyntheticEvent,
    checked: boolean
  ) => {
    setConfirmationDismissed(checked);
  };

  const {
    canAdd,
    customVocabulary,
    maximumWords,
    createCustomVocabulary,
    deleteCustomVocabulary,
    updateCustomVocabulary,
  } = useCustomVocabulary();

  const handleSave = (
    vocab: PostCustomVocabularyBody[],
    onSuccess: () => void
  ) => {
    createCustomVocabulary.mutate(vocab, {
      onSuccess: () => {
        showToast({
          severity: "success",
          message: `${pluralize("Custom word", vocab.length)} added`,
        });
        setAddModalOpen(false);
        onSuccess();
      },
      onError: () =>
        showToast({
          severity: "error",
          message: `Something went wrong and ${
            vocab.length > 1 ? "one or more words" : "the word"
          } could not be saved. Please try again later or reach out to support@read.ai if the problem persists.`,
        }),
    });
  };

  const deleteWord = (id: CustomVocabulary["id"]) => {
    deleteCustomVocabulary.mutate(id, {
      onSuccess: () => {
        showToast({
          severity: "success",
          message: "Custom word deleted",
        });
        setWordIdToConfirmDelete(null);
      },
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Something went wrong and the word could not be deleted. Please try again or reach out to support@read.ai if the problem persists.",
        }),
    });
  };

  const handleUpdateWord = (updatedWord: PatchCustomVocabularyBody) => {
    updateCustomVocabulary.mutate(updatedWord, {
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Something went wrong and the word could not be deleted. Please try again or reach out to support@read.ai if the problem persists.",
        }),
    });
  };

  const handleDeleteWord = (id: CustomVocabulary["id"]) => {
    if (inWorkspace && !confirmationDismissed) {
      setWordIdToConfirmDelete(id);
    } else {
      deleteWord(id);
    }
  };

  const toggleAddModalOpen = () => setAddModalOpen((open) => !open);

  return (
    <>
      <Alert
        severity="info"
        embedded
        sx={{ color: "shades.neutral.600", width: "fit-content" }}
      >
        {inWorkspace && "Custom vocabulary is shared across your Workspace. "}
        Maximum 100 entries.
      </Alert>
      {isLoading ? (
        <Stack variant="center" sx={{ height: "100px" }}>
          <LoadSpinner />
        </Stack>
      ) : (
        <>
          <Typography variant="h3" sx={{ pt: 0.5, fontWeight: 500 }}>
            {pluralizePhrase(customVocabulary.length, "custom word")}
          </Typography>
          {customVocabulary.length > 0 && (
            <CustomVocabularyTable
              onDeleteWord={handleDeleteWord}
              onUpdateWord={handleUpdateWord}
              vocabulary={customVocabulary}
            />
          )}
          <Row>
            <TooltipWrapper
              show={!canAdd}
              title={`${
                inWorkspace ? "Your Workspace has" : "You have"
              } added the maximum of ${maximumWords} custom words`}
            >
              <Button
                disabled={!canAdd}
                startIcon={<PlusCircleIcon />}
                onClick={toggleAddModalOpen}
              >
                Add new
              </Button>
            </TooltipWrapper>
          </Row>
        </>
      )}

      {!isLoading && (
        <>
          <AddCustomVocabularyModal
            allVocabulary={customVocabulary}
            inWorkspace={inWorkspace}
            isSaving={createCustomVocabulary.isPending}
            open={addModalOpen}
            onClose={toggleAddModalOpen}
            onSave={handleSave}
          />
          {inWorkspace && (
            <DeleteWorkspaceWordConfirmationModal
              confirmationDismissed={confirmationDismissed}
              loading={deleteCustomVocabulary.isPending}
              open={wordIdToConfirmDelete != null}
              onClose={() => setWordIdToConfirmDelete(null)}
              onConfirm={() => deleteWord(wordIdToConfirmDelete!)}
              onDismissConfirmation={handleDismissConfirmation}
            />
          )}
        </>
      )}
    </>
  );
};

export default CustomVocabularyModuleContent;
